import React from 'react';
import './index.scss';

const Terms = () => {
  return (
    <div className='legal-page'>
      <div className='legal-container'>
        <div className='legal-header'>
          <h1>Terms of Use & SMS Notification Consent</h1>
          <p className='legal-meta'>Last updated: May 14, 2026</p>
        </div>

        <div className='legal-section'>
          <h2>About CinchLo</h2>
          <p>
            CinchLo is an internal pipeline management application operated by
            FM Home Loans, LLC (also doing business as MR Capital Group), a New
            York–licensed residential mortgage lender (NMLS ID: 2212). CinchLo
            is provided exclusively for use by FM Home Loans employees and
            authorized contractors who have authenticated with their FM Home
            Loans Microsoft 365 corporate credentials. CinchLo is not accessible
            to the general public, mortgage applicants, borrowers, or other
            consumers.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Eligibility & Access</h2>
          <p>
            Access to CinchLo is limited to active FM Home Loans personnel.
            Authentication is performed through Microsoft Entra ID single
            sign-on using corporate credentials. Authorization within the
            application is inherited from each user's Encompass roles and access
            groups. Disabled or terminated employee accounts lose CinchLo access
            automatically.
          </p>
        </div>

        <div className='legal-section'>
          <h2>SMS Notifications — Consent & Disclosure</h2>
          <p>
            FM Home Loans operates CinchLo's SMS notification feature using the
            toll-free phone number 1-877-740-4425. CinchLo uses this number to
            send transactional alerts to FM Home Loans employees about loans
            they are actively working on.
          </p>

          <h3>Message types</h3>
          <p>
            Messages sent by CinchLo include the following categories of
            internal employee alerts:
          </p>
          <ul>
            <li>
              Closing disclosure (CD) and CD change-of-circumstance status
              notifications and deadline reminders
            </li>
            <li>
              Appraisal acknowledgment and appraisal delivery status
              notifications
            </li>
            <li>Rate lock expiration warnings</li>
            <li>Underwriting condition aging alerts</li>
            <li>Pipeline stage and milestone progression notifications</li>
            <li>Other time-sensitive loan-status events</li>
          </ul>

          <h3>Message frequency</h3>
          <p>
            SMS frequency varies based on each employee's individual loan
            pipeline activity. Typical range is 0–5 messages per recipient per
            business day.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Opt-in</h2>
          <p>
            SMS notifications are off by default. No employee receives CinchLo
            SMS messages without first taking explicit action to enable them. To
            receive SMS notifications from CinchLo, an employee must:
          </p>
          <ol>
            <li>
              Be authenticated to CinchLo using their FM Home Loans Microsoft
              365 corporate credentials;
            </li>
            <li>
              Navigate to the Notification Preferences page within CinchLo;
            </li>
            <li>Provide a mobile phone number;</li>
            <li>
              Explicitly enable the SMS delivery channel for one or more
              notification event types by ticking the SMS option and saving
              preferences.
            </li>
          </ol>
          <p>
            By completing these steps, the employee consents to receive
            recurring transactional SMS messages from FM Home Loans at the
            provided mobile number, at the frequencies and for the message types
            described above. Message and data rates may apply per the employee's
            mobile carrier plan.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Opt-out</h2>
          <p>
            You may opt out of all CinchLo SMS notifications at any time by:
          </p>
          <ul>
            <li>
              Replying <code>STOP</code> to any SMS message received from
              1-877-740-4425; or
            </li>
            <li>
              Disabling the SMS delivery channel in your CinchLo Notification
              Preferences page.
            </li>
          </ul>
          <p>
            After opting out, FM Home Loans may send a single confirmation
            message confirming the opt-out and will not send further SMS
            messages to that number unless and until you re-enable SMS through
            CinchLo.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Help</h2>
          <p>
            Reply <code>HELP</code> to any message received from 1-877-740-4425
            to receive contact instructions. For application questions or
            support, contact FM Home Loans IT through the support channels
            listed below.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Carrier liability</h2>
          <p>
            Wireless carriers are not liable for delayed or undelivered
            messages.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Privacy of mobile numbers</h2>
          <p>
            Mobile phone numbers collected through CinchLo are used solely for
            the delivery of CinchLo transactional notifications to the employee
            who provided the number. FM Home Loans does not share, sell, rent,
            or otherwise disclose mobile numbers collected through CinchLo to
            third parties for marketing purposes. See CinchLo's Privacy Policy
            for details on how CinchLo handles personal information, and FM Home
            Loans' general Privacy Policy at{' '}
            <a
              href='https://www.fmm.com/privacy'
              target='_blank'
              rel='noopener noreferrer'
            >
              www.fmm.com/privacy
            </a>{' '}
            for broader privacy practices.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Consent recordkeeping</h2>
          <p>
            When an employee opts in to SMS notifications, CinchLo captures and
            retains an audit log entry that includes: the employee's Entra user
            identifier, a timestamp, the IP address used at opt-in, the
            notification event types enabled, the mobile phone number provided,
            and the exact consent text presented to the employee at the time of
            opt-in.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Acceptable Use of CinchLo</h2>
          <p>
            CinchLo displays sensitive borrower and loan information. By
            accessing CinchLo, you agree to:
          </p>
          <ul>
            <li>
              Use CinchLo only for legitimate business purposes related to your
              role at FM Home Loans;
            </li>
            <li>
              Not share your CinchLo credentials, session tokens, or any content
              accessed through CinchLo with unauthorized parties;
            </li>
            <li>
              Comply with FM Home Loans' information security policies and all
              applicable laws and regulations, including but not limited to the
              Gramm-Leach-Bliley Act, the Fair Credit Reporting Act, 23 NYCRR
              Part 500, and applicable state mortgage lending regulations;
            </li>
            <li>
              Promptly report any suspected unauthorized access or security
              incident to FM Home Loans IT and the Chief Information Security
              Officer.
            </li>
          </ul>
        </div>

        <div className='legal-section'>
          <h2>Logging & Monitoring</h2>
          <p>
            CinchLo logs authentication events, data access events,
            administrative actions, and notification delivery events for
            security, compliance, audit, and operational purposes. By using
            CinchLo, you acknowledge that your activity within the application
            is logged and may be reviewed by FM Home Loans personnel.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Changes to These Terms</h2>
          <p>
            FM Home Loans may update these Terms from time to time. Material
            changes will be communicated through CinchLo and/or corporate email.
            Continued use of CinchLo following the effective date of an updated
            version constitutes acceptance of the revised Terms.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Contact</h2>
          <div className='legal-contact'>
            <p>FM Home Loans, LLC</p>
            <p>2329 Nostrand Avenue</p>
            <p>Brooklyn, NY</p>
            <br />
            <p>
              Support: <a href='mailto:support@fmm.com'>support@fmm.com</a>
            </p>
            <p>Chief Information Security Officer: Asher Fhima</p>
            <p>
              Privacy:{' '}
              <a
                href='https://cinch.fmm.com/privacy'
                target='_blank'
                rel='noopener noreferrer'
              >
                cinch.fmm.com/privacy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
